import React, { ChangeEvent, FC, useEffect, useState } from "react";

import { ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";

import MultiValueTextFieldGroup from "../../../../components/MultiValueTextFieldGroup";
import { ReduxHiddenDataField } from "../../../../components/Redux/ReduxHiddenDataField";
import StringMapInput from "../../../../components/Redux/StringMapInput";
import { ReduxTextField } from "../../../../components/Redux/TextField";
import SettingSection from "../../../../components/SettingSection";
import TextFieldValidationAdornment from "../../../../components/TextFieldValidationAdornment";
import {
  getSettingsFormProperty,
  setSettingsFormError,
  setSettingsFormProperty,
  unsetSettingsFormProperties,
} from "../../../../slices/settings";
import { useDispatch, useSelector } from "../../../../store";
import {
  IdentityProviderType,
  StringList,
  StringMap,
  useCheckIdentityProviderSettingsMutation,
  useGetClusterQuery,
} from "../../../../store/pomeriumApi";
import { validUrl } from "../../../../validators/url";
import { SectionProps } from "../../../routes/components/edit_sections/common";
import AuthenticateServiceUrl from "../AuthenticateServiceUrl";
import IdentityProviderSelector from "../IdentityProviderSelector";
import { IdentityProvidersLookup } from "../IdentityProviders";

type IdentityProviderProps = SectionProps;
const IdentityProvider: FC<IdentityProviderProps> = ({
  readonly,
  tabValue,
  refresh,
}) => {
  const dispatch = useDispatch();
  const [mode, setMode] = useState<"hosted" | "custom">("hosted");
  const authenticateServiceUrl = useSelector(
    getSettingsFormProperty("authenticateServiceUrl", undefined),
  ) as string;
  const identityProvider = useSelector(
    getSettingsFormProperty("identityProvider", ""),
  ) as string;
  const identityProviderClientId = useSelector(
    getSettingsFormProperty("identityProviderClientId", undefined),
  ) as string;
  const identityProviderClientSecret = useSelector(
    getSettingsFormProperty("identityProviderClientSecret", undefined),
  ) as string;
  const identityProviderRequestParams = useSelector(
    getSettingsFormProperty("identityProviderRequestParams", undefined),
  ) as StringMap;
  const identityProviderScopes = useSelector(
    getSettingsFormProperty("identityProviderScopes", undefined),
  ) as StringList;
  const identityProviderUrl = useSelector(
    getSettingsFormProperty("identityProviderUrl", undefined),
  ) as string;
  const checkIdentityProviderSettingsRequest = {
    provider: identityProvider as IdentityProviderType,
    clientId: identityProviderClientId,
    clientSecret: identityProviderClientSecret,
    requestParams: identityProviderRequestParams,
    scopes: identityProviderScopes,
    url: identityProviderUrl,
  };
  const { organizationId, clusterId } = useSelector((state) => state.current);

  const [
    checkIdentityProviderSettings,
    {
      isLoading: checkingIdentityProviderSettings,
      data: checkIdentityProviderSettingsResult,
    },
  ] = useCheckIdentityProviderSettingsMutation();
  const { data: cluster } = useGetClusterQuery(
    { organizationId, clusterId },
    { skip: !organizationId || !clusterId },
  );

  useEffect(() => {
    if (identityProvider) {
      setMode("custom");
    }
  }, [identityProvider]);
  useEffect(() => {
    if (identityProvider) {
      checkIdentityProviderSettings(checkIdentityProviderSettingsRequest);
    }
  }, [JSON.stringify(checkIdentityProviderSettingsRequest)]);

  const handleChangeIdentityProvider = (value: string) => {
    dispatch(
      setSettingsFormProperty({
        path: "identityProvider",
        value: value || undefined,
      }),
    );

    // #2492 clear the identity provider URL when changing identity providers
    dispatch(
      unsetSettingsFormProperties({
        paths: ["identityProviderUrl"],
      }),
    );

    // when changing to an idp, set the authenticate service url to a recommendation based on the cluster
    if (!authenticateServiceUrl && cluster?.fqdn) {
      dispatch(
        setSettingsFormProperty({
          path: "authenticateServiceUrl",
          value: `https://authenticate.${cluster.fqdn}`,
        }),
      );
    }
  };
  const handleChangeMode = (evt: ChangeEvent<HTMLInputElement>) => {
    if (evt.target.value === "hosted") {
      // when changing to hosted, clear all the existing settings
      setMode("hosted");
      dispatch(
        unsetSettingsFormProperties({
          paths: [
            "identityProvider",
            "identityProviderClientId",
            "identityProviderClientSecret",
            "identityProviderRequestParams",
            "identityProviderScopes",
            "identityProviderUrl",
          ],
        }),
      );
    } else {
      setMode("custom");
    }
    setMode(evt.target.value === "hosted" ? "hosted" : "custom");
  };

  return (
    <Container component={Paper} maxWidth={false} disableGutters={true}>
      <Stack key={tabValue + refresh} spacing={1} sx={{ m: 3 }}>
        <Typography id={tabValue} variant="h2" sx={{ pt: 2 }}>
          Identity Provider
        </Typography>
        <Divider />
        <SettingSection
          label="Authenticate Service URL"
          docsKey="authenticate-service-url"
        >
          <AuthenticateServiceUrl readonly={readonly} />
        </SettingSection>
        <SettingSection label="Identity Provider">
          <Stack alignItems="center">
            <FormControl>
              <RadioGroup row value={mode} onChange={handleChangeMode}>
                <FormControlLabel
                  value="hosted"
                  control={<Radio readOnly={readonly} />}
                  label="Hosted Authenticate"
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio readOnly={readonly} />}
                  label="Custom Identity Provider"
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </SettingSection>
        {mode === "hosted" && (
          <>
            <Typography variant="body2" sx={{ p: 4, textAlign: "center" }}>
              The Hosted Authenticate Service is an identity provider you can
              start using right away. It's good for hobbyist deployments and for
              getting started with Pomerium.
            </Typography>
          </>
        )}
        {mode === "custom" && (
          <>
            <SettingSection
              label="Identity Provider"
              docsKey="identity-provider-name"
              extraElement={
                <>
                  {identityProvider && (
                    <Typography variant="caption">
                      <Link
                        target="_blank"
                        href={
                          IdentityProvidersLookup.get(identityProvider)?.docs ||
                          ""
                        }
                      >
                        How do I set up{" "}
                        {IdentityProvidersLookup.get(identityProvider)?.name} as
                        an identity provider for Pomerium?
                      </Link>
                    </Typography>
                  )}
                </>
              }
            >
              <IdentityProviderSelector
                readonly={readonly}
                value={identityProvider}
                onChange={handleChangeIdentityProvider}
              />
            </SettingSection>

            {IdentityProvidersLookup.get(identityProvider)?.providerUrl !==
              "restricted" && (
              <>
                <Divider />
                <SettingSection
                  label="Identity Provider URL"
                  docsKey="identity-provider-url"
                >
                  <ReduxTextField
                    disabled={readonly}
                    setter={setSettingsFormProperty}
                    getter={getSettingsFormProperty}
                    errorHandler={setSettingsFormError}
                    path="identityProviderUrl"
                    validator={validUrl}
                    fullWidth
                    InputProps={{
                      endAdornment: !checkingIdentityProviderSettings && (
                        <TextFieldValidationAdornment
                          error={
                            checkIdentityProviderSettingsResult?.errors?.url
                          }
                        />
                      ),
                    }}
                  />
                </SettingSection>
              </>
            )}

            <Divider />
            <SettingSection
              label="Client ID"
              docsKey="identity-provider-client-id"
            >
              <ReduxTextField
                disabled={readonly}
                setter={setSettingsFormProperty}
                getter={getSettingsFormProperty}
                errorHandler={setSettingsFormError}
                path="identityProviderClientId"
                fullWidth
                InputProps={{
                  endAdornment: !checkingIdentityProviderSettings && (
                    <TextFieldValidationAdornment
                      error={
                        checkIdentityProviderSettingsResult?.errors?.clientId
                      }
                    />
                  ),
                }}
              />
            </SettingSection>

            <Divider />
            <SettingSection
              label="Client Secret"
              docsKey="identity-provider-client-secret"
            >
              <ReduxHiddenDataField
                disabled={readonly}
                setter={setSettingsFormProperty}
                getter={getSettingsFormProperty}
                errorHandler={setSettingsFormError}
                path="identityProviderClientSecret"
                fullWidth
                InputProps={{
                  endAdornment: !checkingIdentityProviderSettings && (
                    <TextFieldValidationAdornment
                      error={
                        checkIdentityProviderSettingsResult?.errors
                          ?.clientSecret
                      }
                    />
                  ),
                }}
              />
            </SettingSection>

            <Divider />

            <Accordion disableGutters elevation={0}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ p: 0 }}>
                <Typography variant="h6">Advanced</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Divider />
                <SettingSection
                  label="Request Params"
                  docsKey="request-parameters"
                >
                  <StringMapInput
                    readonly={readonly}
                    setter={setSettingsFormProperty}
                    getter={getSettingsFormProperty}
                    path={"identityProviderRequestParams"}
                  />
                </SettingSection>

                <Divider />
                <SettingSection
                  label="Scopes"
                  docsKey="identity-provider-scopes"
                >
                  <MultiValueTextFieldGroup
                    disabled={readonly}
                    values={identityProviderScopes || []}
                    onSave={(values) => {
                      dispatch(
                        setSettingsFormProperty({
                          path: "identityProviderScopes",
                          value: values.length === 0 ? undefined : values,
                        }),
                      );
                    }}
                  />
                </SettingSection>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Stack>
    </Container>
  );
};
export default IdentityProvider;
